import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { medicationsAPI } from '../api/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Camera, 
  CheckCircle, 
  Loader2, 
  ChevronLeft, 
  ChevronDown, 
  Coffee, 
  Salad, 
  UtensilsCrossed, 
  Moon,
  Clock,
  AlertCircle
} from 'lucide-react';
// Tesseract.js removed - now using direct GPT-4o-mini vision
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const AddMedicationScreen = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [photoUrl, setPhotoUrl] = useState('');
  const [aiInterpretation, setAiInterpretation] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showAiDetails, setShowAiDetails] = useState(false);

  // Multi-medication state
  const [detectedMedications, setDetectedMedications] = useState([]);
  const [currentMedIndex, setCurrentMedIndex] = useState(0);
  
  // Current medication being edited
  const [customMedicationName, setCustomMedicationName] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [selectedTimings, setSelectedTimings] = useState([]);
  const [frequency, setFrequency] = useState(1);
  const [mealTiming, setMealTiming] = useState('after');

  const fileInputRef = useRef(null);

  // Comprehensive medication list
  const commonMedications = [
    'Paracetamol (Tylenol)', 'Aspirin', 'Ibuprofen (Advil, Motrin)', 'Naproxen (Aleve)', 'Tramadol',
    'Amlodipine', 'Atenolol', 'Bisoprolol', 'Carvedilol', 'Enalapril', 'Losartan', 'Metoprolol', 
    'Ramipril', 'Valsartan', 'Atorvastatin (Lipitor)', 'Simvastatin', 'Rosuvastatin (Crestor)',
    'Metformin (Glucophage)', 'Gliclazide', 'Glimepiride', 'Insulin', 'Sitagliptin (Januvia)',
    'Omeprazole (Prilosec)', 'Pantoprazole', 'Ranitidine', 'Esomeprazole (Nexium)',
    'Levothyroxine (Synthroid)', 'Thyroxine', 'Warfarin (Coumadin)', 'Clopidogrel (Plavix)', 
    'Apixaban (Eliquis)', 'Amoxicillin', 'Azithromycin', 'Ciprofloxacin', 'Furosemide (Lasix)',
    'Prednisone', 'Allopurinol', 'Gabapentin',
  ];

  const timingOptions = [
    { value: 'before_breakfast', label: 'Before Breakfast', icon: Coffee },
    { value: 'breakfast', label: 'After Breakfast', icon: Coffee },
    { value: 'before_lunch', label: 'Before Lunch', icon: Salad },
    { value: 'lunch', label: 'After Lunch', icon: Salad },
    { value: 'before_dinner', label: 'Before Dinner', icon: UtensilsCrossed },
    { value: 'dinner', label: 'After Dinner', icon: UtensilsCrossed },
    { value: 'bed', label: 'Before Sleep', icon: Moon },
    { value: 'prn', label: 'As Needed', icon: Clock },
  ];

  const processWithLLM = async (imageBase64) => {
    try {
      console.log('🔄 Sending image to Gemini 2.5 Flash for multi-medication extraction...');
      console.log('📷 Image size:', Math.round(imageBase64.length / 1024), 'KB');

      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('medbuddy_token');
      
      const response = await fetch(`${BACKEND_URL}/api/process-ocr`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          image_base64: imageBase64
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Backend error:', errorData);
        throw new Error(errorData.detail || 'Failed to process image');
      }

      const parsed = await response.json();
      console.log('🔍 Gemini 2.5 Flash Output:', JSON.stringify(parsed, null, 2));
      console.log(`✅ Detected ${parsed.total_detected} medication(s)`);
      
      return parsed;
    } catch (error) {
      console.error('❌ Image processing error:', error);
      return null;
    }
  };

  const matchMedication = (extractedName) => {
    if (!extractedName) return { matched: null, confidence: 'low' };
    
    const nameLower = extractedName.toLowerCase().trim();
    
    // Exact match
    const exactMatch = commonMedications.find(med => 
      med.toLowerCase() === nameLower
    );
    if (exactMatch) return { matched: exactMatch, confidence: 'high' };
    
    // Contains match
    const containsMatch = commonMedications.find(med => {
      const medName = med.split('(')[0].trim().toLowerCase();
      return nameLower.includes(medName) || medName.includes(nameLower);
    });
    
    if (containsMatch) {
      const medName = containsMatch.split('(')[0].trim().toLowerCase();
      const similarity = nameLower.includes(medName) ? 'high' : 'medium';
      return { matched: containsMatch, confidence: similarity };
    }
    
    return { matched: extractedName, confidence: 'low' };
  };

  const mapTimingToSlots = (freq, mealTime, timeOfDay) => {
    const prefix = mealTime === 'before' ? 'before_' : '';
    
    // If time of day is specified, use it to determine slot
    if (timeOfDay && timeOfDay !== 'unknown') {
      const timeStr = timeOfDay.toLowerCase();
      if (timeStr.includes('morning')) {
        return [prefix + 'breakfast'];
      } else if (timeStr.includes('afternoon') || timeStr.includes('noon')) {
        return [prefix + 'lunch'];
      } else if (timeStr.includes('evening')) {
        return [prefix + 'dinner'];
      } else if (timeStr.includes('night') || timeStr.includes('bed')) {
        return ['bed'];
      }
    }
    
    // Otherwise use frequency-based defaults
    if (freq === 1) {
      return [prefix + 'breakfast'];
    } else if (freq === 2) {
      return [prefix + 'breakfast', prefix + 'dinner'];
    } else if (freq === 3) {
      return [prefix + 'breakfast', prefix + 'lunch', prefix + 'dinner'];
    } else if (freq === 4) {
      return ['breakfast', 'lunch', 'dinner', 'bed'];
    }
    return [prefix + 'breakfast'];
  };

  // Helper to load a medication into the edit form
  const loadMedicationIntoForm = (med) => {
    if (!med) return;
    
    setCustomMedicationName(med.medication_name || '');
    setShowCustomInput(true);
    
    // Set frequency
    let freq = 1;
    if (med.frequency_per_day && med.frequency_per_day !== 'unknown') {
      freq = parseInt(med.frequency_per_day) || 1;
    }
    setFrequency(freq);
    
    // Determine meal timing
    let mealTime = 'after';
    if (med.timing_relation_to_meals) {
      const timing = med.timing_relation_to_meals.toLowerCase();
      if (timing.includes('before')) mealTime = 'before';
    }
    setMealTiming(mealTime);
    
    // Map to timing slots
    const slots = mapTimingToSlots(freq, mealTime, null);
    setSelectedTimings(slots);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setProcessing(true);

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result;
        setPhotoUrl(base64String);

        try {
          console.log('📷 Sending image to Claude Haiku 4 for analysis...');
          const result = await processWithLLM(base64String);
          
          if (result && result.medications && result.medications.length > 0) {
            console.log(`✅ Detected ${result.total_detected} medication(s)`);
            
            // Store full AI response
            setAiInterpretation(result);
            setDetectedMedications(result.medications);
            setCurrentMedIndex(0);
            
            // Load first medication into form
            loadMedicationIntoForm(result.medications[0]);
            
            // Show success message
            if (result.total_detected === 1) {
              toast.success('1 medication detected!', { duration: 3000 });
            } else {
              toast.success(`${result.total_detected} medications detected! Review each one.`, { duration: 4000 });
            }
          } else {
            // No medications detected
            setDetectedMedications([]);
            setShowCustomInput(true);
            toast.warning('No medications detected. Please enter details manually.');
          }
        } catch (aiError) {
          console.error('AI Vision error:', aiError);
          toast.error('Could not analyze image. Please enter details manually.');
          setDetectedMedications([]);
          setShowCustomInput(true);
        }

        setProcessing(false);
        setStep(2);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('File error:', error);
      toast.error('Error processing image');
      setProcessing(false);
    }
  };

  const handleFrequencyChange = (newFrequency) => {
    const freq = parseInt(newFrequency);
    setFrequency(freq);
    
    if (selectedTimings.includes('prn')) return;
    
    const slots = mapTimingToSlots(freq, mealTiming, null);
    setSelectedTimings(slots);
  };

  const handleMealTimingChange = (newMealTiming) => {
    setMealTiming(newMealTiming);
    
    if (selectedTimings.includes('prn')) return;
    
    const slots = mapTimingToSlots(frequency, newMealTiming, null);
    setSelectedTimings(slots);
  };

  const toggleTiming = (timing) => {
    if (timing === 'prn') {
      setSelectedTimings(['prn']);
      return;
    }

    if (selectedTimings.includes('prn')) {
      setSelectedTimings([timing]);
      return;
    }

    if (selectedTimings.includes(timing)) {
      setSelectedTimings(selectedTimings.filter((t) => t !== timing));
    } else {
      setSelectedTimings([...selectedTimings, timing]);
    }
  };

  const handleSubmit = async () => {
    const finalName = customMedicationName;

    if (!finalName) {
      toast.error('Please enter medication name');
      return;
    }

    if (selectedTimings.length === 0) {
      toast.error('Please select at least one timing');
      return;
    }

    try {
      // Get instruction text from current detected medication if available
      const currentMed = detectedMedications[currentMedIndex];
      const instructionText = currentMed?.raw_instruction_summary || null;
      
      await medicationsAPI.create({
        display_name: finalName,
        instructions_text: instructionText,
        routine_slots: selectedTimings,
        frequency_per_day: frequency,
        photo_url: photoUrl || null,
      });

      // Check if there are more medications to process
      if (currentMedIndex < detectedMedications.length - 1) {
        // Move to next medication
        const nextIndex = currentMedIndex + 1;
        setCurrentMedIndex(nextIndex);
        loadMedicationIntoForm(detectedMedications[nextIndex]);
        toast.success(`Saved! Now review medication ${nextIndex + 1} of ${detectedMedications.length}`);
      } else {
        // All medications saved
        const totalSaved = detectedMedications.length || 1;
        toast.success(totalSaved > 1 ? `All ${totalSaved} medications added!` : 'Medication added!');
        navigate('/meds');
      }
    } catch (error) {
      console.error('Error creating medication:', error);
      toast.error('Failed to add medication');
    }
  };
  
  // Skip current medication and move to next
  const handleSkipMedication = () => {
    if (currentMedIndex < detectedMedications.length - 1) {
      const nextIndex = currentMedIndex + 1;
      setCurrentMedIndex(nextIndex);
      loadMedicationIntoForm(detectedMedications[nextIndex]);
      toast.info(`Skipped. Now review medication ${nextIndex + 1} of ${detectedMedications.length}`);
    } else {
      toast.info('No more medications to review');
      navigate('/meds');
    }
  };

  return (
    <div
      className="screen-container bg-gradient-to-b from-green-50 to-white min-h-screen"
      data-testid="add-medication-screen"
    >
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4 mb-6">
          <Button
            data-testid="btn-back"
            variant="ghost"
            size="icon"
            onClick={() => (step > 1 ? setStep(step - 1) : navigate(-1))}
            className="h-12 w-12"
          >
            <ChevronLeft className="h-8 w-8" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Add Medication</h1>
            <p className="text-lg text-gray-600">Step {step} of 2</p>
          </div>
        </div>

        {step === 1 && (
          <div className="space-y-6" data-testid="step-capture-photo">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Take a photo of your medication</h2>
            <p className="text-lg text-gray-600 mb-6">
              Capture the label, box, or pharmacy sticker for easy setup
            </p>

            <div className="flex items-center justify-between bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-6">
              <div>
                <p className="font-semibold text-gray-900">Language: English</p>
                <p className="text-sm text-gray-600">AI-powered smart detection</p>
              </div>
            </div>

            {processing ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-16 w-16 animate-spin text-green-600 mb-4" />
                <p className="text-xl font-semibold text-gray-600">Processing photo...</p>
                <p className="text-base text-gray-500 mt-2">AI is reading and validating details...</p>
              </div>
            ) : (
              <>
                {/* Camera input - opens camera directly */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input-camera"
                />
                {/* Gallery input - opens photo picker */}
                <input
                  id="gallery-input"
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input-gallery"
                />
                
                <Button
                  data-testid="btn-take-photo"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full h-20 text-xl font-bold bg-green-600 hover:bg-green-700"
                >
                  <Camera className="mr-3 h-8 w-8" />
                  Take Photo
                </Button>
                
                <Button
                  data-testid="btn-choose-gallery"
                  variant="outline"
                  onClick={() => document.getElementById('gallery-input')?.click()}
                  className="w-full h-14 text-lg font-semibold mt-3"
                >
                  Choose from Gallery
                </Button>
                
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-gradient-to-b from-green-50 to-white px-4 text-gray-500">or</span>
                  </div>
                </div>
                
                <Button
                  data-testid="btn-skip-photo"
                  variant="outline"
                  onClick={() => {
                    setDetectedMedications([]);
                    setShowCustomInput(true);
                    setStep(2);
                  }}
                  className="w-full h-16 text-lg font-semibold border-2 border-gray-300 hover:border-green-500"
                >
                  Enter Details Manually
                </Button>
              </>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6" data-testid="step-review-suggestions">
            {/* Multi-medication progress indicator */}
            {detectedMedications.length > 1 && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                <p className="text-lg font-semibold text-blue-800">
                  Medication {currentMedIndex + 1} of {detectedMedications.length}
                </p>
                <div className="flex gap-2 mt-2">
                  {detectedMedications.map((_, idx) => (
                    <div
                      key={idx}
                      className={`h-2 flex-1 rounded-full ${
                        idx < currentMedIndex ? 'bg-green-500' :
                        idx === currentMedIndex ? 'bg-blue-500' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}
            
            <h2 className="text-2xl font-bold text-gray-900">
              {detectedMedications.length > 1 ? 'Review This Medication' : 'Review Details'}
            </h2>

            {photoUrl && (
              <Card>
                <CardContent className="p-4">
                  <img
                    src={photoUrl}
                    alt="Medication"
                    className="w-full rounded-xl object-contain mb-4 border border-gray-200"
                    style={{ maxHeight: '200px' }}
                  />
                  
                  {aiInterpretation && (
                    <div className="space-y-3">
                      <button
                        data-testid="btn-toggle-ai-details"
                        onClick={() => setShowAiDetails(!showAiDetails)}
                        className="text-sm font-semibold text-blue-600 hover:underline flex items-center gap-2"
                      >
                        {showAiDetails ? 'Hide' : 'Show'} AI detection details
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${showAiDetails ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {showAiDetails && (
                        <div className="mt-2 p-3 bg-blue-50 rounded-lg text-sm text-gray-800 max-h-48 overflow-y-auto">
                          <pre className="whitespace-pre-wrap font-mono text-xs">
                            {JSON.stringify(detectedMedications[currentMedIndex] || aiInterpretation, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Medication Name - Direct input */}
            <div className="space-y-2">
              <Label className="text-lg font-semibold">Medication Name *</Label>
              {detectedMedications[currentMedIndex]?.confidence_level && (
                <p className="text-sm text-gray-600">
                  AI Confidence: <span className={`font-semibold ${
                    detectedMedications[currentMedIndex].confidence_level === 'high' ? 'text-green-600' :
                    detectedMedications[currentMedIndex].confidence_level === 'medium' ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {detectedMedications[currentMedIndex].confidence_level.toUpperCase()}
                  </span>
                </p>
              )}
              
              <Input
                data-testid="input-custom-medication"
                type="text"
                value={customMedicationName}
                onChange={(e) => setCustomMedicationName(e.target.value)}
                placeholder="Enter medication name"
                className="h-14 text-lg"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-lg font-semibold">How many times per day?</Label>
              <Select value={frequency.toString()} onValueChange={handleFrequencyChange}>
                <SelectTrigger data-testid="select-frequency" className="h-14 text-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1" className="text-lg py-3">1x per day</SelectItem>
                  <SelectItem value="2" className="text-lg py-3">2x per day</SelectItem>
                  <SelectItem value="3" className="text-lg py-3">3x per day</SelectItem>
                  <SelectItem value="4" className="text-lg py-3">4x per day</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-lg font-semibold">Before or after meals?</Label>
              <RadioGroup value={mealTiming} onValueChange={handleMealTimingChange}>
                <div className="grid grid-cols-2 gap-3">
                  <div 
                    className={`flex items-center space-x-2 p-4 rounded-xl border-2 cursor-pointer ${
                      mealTiming === 'before' 
                        ? 'border-green-600 bg-green-50' 
                        : 'border-gray-200 bg-white'
                    }`}
                    onClick={() => handleMealTimingChange('before')}
                  >
                    <RadioGroupItem value="before" id="before" />
                    <Label htmlFor="before" className="text-lg font-semibold cursor-pointer">
                      Before meals
                    </Label>
                  </div>
                  <div 
                    className={`flex items-center space-x-2 p-4 rounded-xl border-2 cursor-pointer ${
                      mealTiming === 'after' 
                        ? 'border-green-600 bg-green-50' 
                        : 'border-gray-200 bg-white'
                    }`}
                    onClick={() => handleMealTimingChange('after')}
                  >
                    <RadioGroupItem value="after" id="after" />
                    <Label htmlFor="after" className="text-lg font-semibold cursor-pointer">
                      After meals
                    </Label>
                  </div>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label className="text-lg font-semibold">When to take?</Label>
              <p className="text-sm text-gray-600 mb-2">
                Based on {frequency}x per day {mealTiming} meals (adjust if needed)
              </p>
              <div className="grid grid-cols-1 gap-3">
                {timingOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <Button
                      key={option.value}
                      data-testid={`timing-${option.value}`}
                      onClick={() => toggleTiming(option.value)}
                      variant={selectedTimings.includes(option.value) ? 'default' : 'outline'}
                      className={`h-16 text-lg font-semibold justify-start ${
                        selectedTimings.includes(option.value)
                          ? 'bg-green-600 hover:bg-green-700 text-white'
                          : ''
                      }`}
                    >
                      <Icon className="mr-3 h-6 w-6" />
                      {selectedTimings.includes(option.value) && '✓ '}
                      {option.label}
                    </Button>
                  );
                })}
              </div>
            </div>

            <Button
              data-testid="btn-confirm"
              onClick={handleSubmit}
              className="w-full h-20 text-xl font-bold bg-green-600 hover:bg-green-700 mt-8"
            >
              <CheckCircle className="mr-3 h-8 w-8" />
              {detectedMedications.length > 1 && currentMedIndex < detectedMedications.length - 1
                ? `Save & Next (${currentMedIndex + 1}/${detectedMedications.length})`
                : 'Confirm & Save'}
            </Button>
            
            {/* Skip button for multi-medication */}
            {detectedMedications.length > 1 && (
              <Button
                data-testid="btn-skip-medication"
                variant="outline"
                onClick={handleSkipMedication}
                className="w-full h-14 text-lg font-semibold border-2 border-gray-300"
              >
                Skip This Medication
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddMedicationScreen;
